/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */

// pcg-viewer canvas: main waveform renderer (core-viewer.js 1859-2210, plus the
// overlapsNoiseWindow helper from 1623-1625).
//
// Extraction discipline: the function body below is a verbatim copy of the
// prototype's drawWave. The SHIM PROLOGUE defines local constants with exactly
// the names the body referenced as globals (D, RAW, MURMUR, view, state,
// analysisQuality, visualGuidePeaks, ...), bound to the render context. The only
// edited body line is the setupCanvas('wave', 350) id lookup, which becomes
// sizeCanvas(rc.canvases.wave, 350). This is the display contract — do not
// "improve" the drawing math.

import { rejectGate, isGateHardRejected } from "../core/gate"
import { visualGuidePeaks as coreVisualGuidePeaks } from "../core/cycles"
import { phaseAlternateActivity } from "../core/phase"
import { tToX as viewTToX, visibleRange as viewVisibleRange } from "./view"
import { sizeCanvas, drawCautionWindow, drawPlayhead as drawPlayheadImpl } from "./draw-common"
import { activeLoopBounds as loopActiveLoopBounds } from "./loop"
import { evaluationGuideRange, outsideEvaluationRange } from "../core/evaluation-range"

export function drawWave(rc) {
  const canvas = rc.canvases.wave
  if (!canvas) return
  // ---- shim prologue (globals -> render context) ----
  const D = rc.payload
  const RAW = rc.traces.raw
  const MURMUR = rc.traces.murmur
  const { view, state } = rc
  const phaseReviewResolution = rc.core.ctx.phaseReviewResolution
  const analysisQuality = () => rc.core.analysisQuality()
  const visualGuidePeaks = () => coreVisualGuidePeaks(D, phaseReviewResolution)
  const visualAcousticActivityStats = () => rc.core.visualAcousticActivityStats()
  // dev.18 Coherent Energy: the Energy layer draws the research-selected band's
  // coherent overlay (state.energyBand) over a grey released-envelope reference.
  const visibleEnergyGuide = () =>
    rc.core.visibleEnergyGuide(state.energyBand, state.energyGuideMode, state.markerOverlapPreview)
  const tToX = (t, pL, pW) => viewTToX(view, t, pL, pW)
  const visibleRange = (len) => viewVisibleRange(view, D.duration, len)
  const overlapsNoiseWindow = (start, end) =>
    (analysisQuality().noiseWindows || []).some((w) => end > w.start && start < w.end)
  const drawPlayhead = (c2, pL, pW, pT, pH, opts) => drawPlayheadImpl(rc, c2, pL, pW, pT, pH, opts)
  // handoff v0.8.3 Loop: the ruler band at the top of the plot shows the
  // independent loop range (state.loopRange, valid only at state.loopRevision).
  const activeLoopBounds = () => loopActiveLoopBounds(state)
  // Timing engine (upstream v0.9.1): with an Evaluation Range (rc.core is the
  // range's metric scope; rc.payload the whole recording's markers), what lies
  // outside the range is drawn dimmed as context. Upstream keys this on its
  // `pcg-exam-v0900` body class and the injected range; here on the core's range.
  const mvp2EvalGuideRange = () => evaluationGuideRange(rc.core.ctx.evaluationRange)
  const mvp2OutsideEvalGuideContext = (t) => outsideEvaluationRange(mvp2EvalGuideRange(), t)
  // ---- end shim prologue; body verbatim below ----
  const { ctx, W, H } = sizeCanvas(canvas, 350)
  const pL = 46,
    pR = 10,
    pT = 30,
    pB = 8,
    pW = W - pL - pR,
    pH = H - pT - pB,
    mY = pT + pH / 2
  ctx.clearRect(0, 0, W, H)
  const tEnd = view.start + view.width
  const quality = analysisQuality()
  const gateRejected = isGateHardRejected(rejectGate(D))

  // Cycle guidance bands are neutral grey in normal review mode.
  if (!gateRejected && state.layers.regions) {
    const peaks = visualGuidePeaks()
    for (let i = 0; i < peaks.length - 1; i++) {
      const a = peaks[i],
        b = peaks[i + 1]
      if (b.t < view.start || a.t > tEnd) continue
      const outsideEvalContext = mvp2OutsideEvalGuideContext((a.t + b.t) / 2)
      const x1 = tToX(a.t, pL, pW),
        x2 = tToX(b.t, pL, pW)
      if (a.l === "S1" && b.l === "S2") {
        ctx.fillStyle = outsideEvalContext ? "rgba(148,163,184,0.045)" : "rgba(148,163,184,0.14)"
        ctx.fillRect(x1, pT, x2 - x1, pH)
        ctx.fillStyle = outsideEvalContext ? "rgba(203,213,225,0.08)" : "rgba(203,213,225,0.24)"
        ctx.fillRect(x1, pT, x2 - x1, 2)
        ctx.fillRect(x1, pT + pH - 2, x2 - x1, 2)
      } else if (a.l === "S2" && b.l === "S1") {
        ctx.fillStyle = outsideEvalContext ? "rgba(100,116,139,0.03)" : "rgba(100,116,139,0.08)"
        ctx.fillRect(x1, pT, x2 - x1, pH)
      }
    }
    // Keep phase labels quiet at normal zoom; exact durations are available on hover.
    if (view.width <= 3.0) {
      ctx.font = "9px sans-serif"
      ctx.textAlign = "left"
      for (let i = 0; i < peaks.length - 1; i++) {
        const a = peaks[i],
          b = peaks[i + 1]
        if (b.t < view.start || a.t > tEnd) continue
        if (state.layers.noise && overlapsNoiseWindow(a.t, b.t)) continue
        const outsideEvalContext = mvp2OutsideEvalGuideContext((a.t + b.t) / 2)
        const x1 = tToX(a.t, pL, pW),
          x2 = tToX(b.t, pL, pW)
        const w = x2 - x1
        if (a.l === "S1" && b.l === "S2" && w > 54) {
          ctx.fillStyle = outsideEvalContext ? "rgba(148,163,184,0.24)" : "rgba(203,213,225,0.54)"
          ctx.fillText("SYS", x1 + 4, pT + 10)
        } else if (a.l === "S2" && b.l === "S1" && w > 58) {
          ctx.fillStyle = outsideEvalContext ? "rgba(148,163,184,0.18)" : "rgba(203,213,225,0.42)"
          ctx.fillText("DIA", x1 + 4, pT + 10)
        }
      }
    }
  }

  // Adaptive grid: tick spacing varies with zoom
  let tickStep
  if (view.width > 12) tickStep = 2
  else if (view.width > 6) tickStep = 1
  else if (view.width > 3) tickStep = 0.5
  else if (view.width > 1.5) tickStep = 0.25
  else tickStep = 0.1
  // The neutral ruler represents the viewport. Its coloured region is the
  // independent loop range, matching timeline editors such as Logic Pro.
  ctx.fillStyle = "rgba(15,20,29,0.46)"
  ctx.fillRect(pL, 0, pW, pT - 4)
  const loopBounds = activeLoopBounds()
  if (loopBounds) {
    const rawX1 = tToX(loopBounds.start, pL, pW)
    const rawX2 = tToX(loopBounds.end, pL, pW)
    const x1 = Math.max(pL, Math.min(pL + pW, rawX1))
    const x2 = Math.max(pL, Math.min(pL + pW, rawX2))
    if (x2 > x1) {
      ctx.fillStyle = "#167f78"
      ctx.fillRect(x1, 0, x2 - x1, pT - 4)
      const gripWidth = 8
      const gripHeight = pT - 4
      function drawLoopGrip(centerX) {
        const gx = Math.round(centerX - gripWidth / 2)
        ctx.fillStyle = "#65a9a3"
        ctx.fillRect(gx, 0, gripWidth, gripHeight)
        ctx.fillStyle = "#285d59"
        for (let stripeX = gx + 2; stripeX < gx + gripWidth - 1; stripeX += 3) {
          ctx.fillRect(stripeX, 3, 1, Math.max(1, gripHeight - 6))
        }
        ctx.strokeStyle = "#8bc4bf"
        ctx.lineWidth = 1
        ctx.strokeRect(gx + 0.5, 0.5, gripWidth - 1, gripHeight - 1)
      }
      if (rawX1 >= pL && rawX1 <= pL + pW) drawLoopGrip(rawX1)
      if (rawX2 >= pL && rawX2 <= pL + pW) drawLoopGrip(rawX2)
    }
  }
  ctx.strokeStyle = "#1f2937"
  ctx.lineWidth = 1
  const tStart = Math.ceil(view.start / tickStep) * tickStep
  for (let t = tStart; t <= tEnd + 1e-6; t += tickStep) {
    const x = tToX(t, pL, pW)
    ctx.beginPath()
    ctx.moveTo(x, pT)
    ctx.lineTo(x, pT + pH)
    ctx.stroke()
    const insideLoop = loopBounds && t >= loopBounds.start && t <= loopBounds.end
    ctx.fillStyle = insideLoop ? "#d5f5f0" : "#9aa6b6"
    ctx.font = "10px sans-serif"
    ctx.textAlign = "center"
    ctx.fillText(t.toFixed(tickStep < 0.5 ? 2 : 1) + "s", x, 16)
  }
  ctx.fillStyle = "#9aa6b6"
  ctx.font = "9px sans-serif"
  ctx.textAlign = "right"
  ctx.fillText("+1.0", pL - 5, pT + 10)
  ctx.fillText("0", pL - 5, mY + 3)
  ctx.fillText("-1.0", pL - 5, pT + pH)
  ctx.strokeStyle = "#1f2937"
  ctx.beginPath()
  ctx.moveTo(pL, mY)
  ctx.lineTo(pL + pW, mY)
  ctx.stroke()

  // Helper: peak abs of array
  function absMax(a) {
    let m = 0
    for (let i = 0; i < a.length; i++) {
      const v = a[i] < 0 ? -a[i] : a[i]
      if (v > m) m = v
    }
    return m
  }
  // Helper: stroke a trace within the visible window
  function plotTrace(arr, peakAbs, stroke, lw, scale) {
    const len = arr.length,
      dt = D.duration / len
    const [i0, i1] = visibleRange(len)
    ctx.strokeStyle = stroke
    ctx.lineWidth = lw
    ctx.beginPath()
    for (let i = i0; i < i1; i++) {
      const x = tToX(i * dt, pL, pW)
      const y = mY - (arr[i] / peakAbs) * (pH / 2) * scale
      i === i0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    }
    ctx.stroke()
  }
  function rangeForBin(arr, tA, tB) {
    const len = arr.length
    if (len < 2) return { mn: 0, mx: 0 }
    const i0 = Math.max(0, Math.floor((tA / D.duration) * len))
    const i1 = Math.min(len - 1, Math.ceil((tB / D.duration) * len))
    if (i1 <= i0) {
      const v = sampleAt(arr, (tA + tB) / 2)
      return { mn: Math.min(0, v), mx: Math.max(0, v) }
    }
    let mn = 0,
      mx = 0
    for (let i = i0; i <= i1; i++) {
      const v = arr[i]
      if (v < mn) mn = v
      if (v > mx) mx = v
    }
    return { mn, mx }
  }
  function plotPeakTrace(arr, peakAbs, stroke, lw, scale) {
    const bins = Math.max(240, Math.min(1400, Math.floor(pW)))
    const binDt = view.width / bins
    ctx.save()
    ctx.lineCap = "butt"
    ctx.strokeStyle = stroke
    ctx.lineWidth = lw
    for (let j = 0; j < bins; j++) {
      const tA = view.start + j * binDt
      const tB = Math.min(tEnd, tA + binDt)
      const { mn, mx } = rangeForBin(arr, tA, tB)
      if (Math.abs(mx - mn) < 1e-8) continue
      const x = tToX((tA + tB) / 2, pL, pW)
      const y1 = mY - (mx / peakAbs) * (pH / 2) * scale
      const y2 = mY - (mn / peakAbs) * (pH / 2) * scale
      ctx.beginPath()
      ctx.moveTo(x, y1)
      ctx.lineTo(x, y2)
      ctx.stroke()
    }
    ctx.restore()
  }
  function sampleAt(arr, t) {
    if (t < 0 || t > D.duration || arr.length < 2) return 0
    const pos = (t / D.duration) * (arr.length - 1)
    const i0 = Math.floor(pos)
    const i1 = Math.min(arr.length - 1, i0 + 1)
    const f = pos - i0
    return arr[i0] * (1 - f) + arr[i1] * f
  }
  function cleanPulseWidth(peak) {
    const eventWidth = Math.max(0, (peak.off || peak.t) - (peak.on || peak.t))
    return Math.max(0.045, Math.min(0.125, eventWidth * 0.58 || 0.065))
  }
  function percentile(vals, q) {
    if (!vals.length) return 0
    const sorted = [...vals].sort((a, b) => a - b)
    const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * q)))
    return sorted[idx]
  }
  function signedCompress(v, peak, power) {
    const s = v < 0 ? -1 : 1
    const a = Math.min(1.8, Math.abs(v) / (peak + 1e-9))
    return s * Math.pow(a, power)
  }
  function plotCleanTrace() {
    const t0 = view.start
    const evalRange = mvp2EvalGuideRange()
    const hasEvalRange = !!evalRange
    function inEvalRange(t) {
      return hasEvalRange ? t >= evalRange.start && t <= evalRange.end : true
    }
    const primaryWaveColor = hasEvalRange ? "rgba(248,250,252,0.92)" : "rgba(203,213,225,0.78)"
    const outsideWaveColor = "rgba(100,116,139,0.38)"
    const baselineColor = "rgba(203,213,225,0.36)"
    const peaks = visualGuidePeaks().filter((p) => p.t >= t0 - 0.25 && p.t <= tEnd + 0.25)
    if (!peaks.length) {
      plotPeakTrace(RAW, absMax(RAW) + 1e-9, "rgba(148,163,184,0.50)", 0.8, 0.7)
      return
    }
    const samples = Math.max(240, Math.min(1100, Math.floor(pW / (view.width <= 6 ? 1.4 : 2.1))))
    const visibleAbs = []
    for (let j = 0; j < samples; j++) {
      const t = t0 + (j / Math.max(1, samples - 1)) * view.width
      visibleAbs.push(Math.abs(sampleAt(RAW, t)))
    }
    const scaleRef = Math.max(percentile(visibleAbs, 0.995), absMax(RAW) * 0.1, 1e-9)
    function guideEmphasis(t) {
      let m = 0
      for (const p of peaks) {
        const sigma = cleanPulseWidth(p) * 0.92
        const z = (t - p.t) / sigma
        if (Math.abs(z) > 3.4) continue
        const confidence = Math.max(0, Math.min(1, Number(p.confidence ?? 0.75)))
        const confidenceWeight = 0.38 + 0.62 * confidence
        const sourceWeight = p.source === "inferred" ? 0.55 : 1.0
        m = Math.max(m, Math.exp(-0.5 * z * z) * sourceWeight * confidenceWeight)
      }
      return 0.075 + 0.925 * Math.min(1, m)
    }
    const rawPeak = absMax(RAW) + 1e-9
    const binDt = view.width / samples
    const subSteps = Math.max(3, Math.min(18, Math.round((RAW.length / D.duration) * binDt)))
    ctx.lineCap = "round"
    ctx.lineWidth = 0.65
    for (let j = 0; j < samples; j += 2) {
      const tc = t0 + (j + 0.5) * binDt
      const x = tToX(tc, pL, pW)
      let mn = 0,
        mx = 0
      for (let k = 0; k < subSteps; k++) {
        const tt = tc - binDt / 2 + (k / Math.max(1, subSteps - 1)) * binDt
        const v = sampleAt(RAW, tt)
        if (v < mn) mn = v
        if (v > mx) mx = v
      }
      const y1 = mY - (mx / rawPeak) * 0.06 * (pH / 2)
      const y2 = mY - (mn / rawPeak) * 0.06 * (pH / 2)
      // Upstream draws this faint layer rgba(148,163,184,0.12) without a range;
      // a guide without one keeps the 0.18 it was drawn with before the engine.
      ctx.strokeStyle = hasEvalRange
        ? inEvalRange(tc)
          ? "rgba(248,250,252,0.18)"
          : "rgba(148,163,184,0.12)"
        : "rgba(148,163,184,0.18)"
      ctx.beginPath()
      ctx.moveTo(x, y1)
      ctx.lineTo(x, y2)
      ctx.stroke()
    }
    ctx.lineWidth = view.width <= 6 ? 1.05 : 0.92
    const cleanBins = []
    for (let j = 0; j < samples; j++) {
      const tc = t0 + (j + 0.5) * binDt
      const x = tToX(tc, pL, pW)
      let mn = 0,
        mx = 0
      for (let k = 0; k < subSteps; k++) {
        const tt = tc - binDt / 2 + (k / Math.max(1, subSteps - 1)) * binDt
        const v = sampleAt(RAW, tt)
        if (v < mn) mn = v
        if (v > mx) mx = v
      }
      const mask = guideEmphasis(tc)
      const rawAmp = Math.max(Math.abs(mx), Math.abs(mn))
      cleanBins.push({ tc, x, rawAmp, mask })
    }
    function shapedEventAmp(binIndex) {
      const center = cleanBins[binIndex]
      const support = view.width <= 6 ? 0.026 : 0.034
      let weighted = 0,
        weights = 0
      for (let q = Math.max(0, binIndex - 12); q < Math.min(cleanBins.length, binIndex + 13); q++) {
        const b = cleanBins[q]
        const dt = Math.abs(b.tc - center.tc)
        if (dt > support) continue
        const w = Math.exp(-0.5 * Math.pow(dt / (support * 0.48), 2))
        weighted += b.rawAmp * b.mask * w
        weights += w
      }
      const localEnvelope = weights > 0 ? weighted / weights : center.rawAmp * center.mask
      return Math.max(center.rawAmp * center.mask, localEnvelope * 0.82)
    }
    for (let j = 0; j < cleanBins.length; j++) {
      const b = cleanBins[j]
      const shapedAmp = shapedEventAmp(j)
      const amp = Math.abs(signedCompress(shapedAmp, scaleRef, 0.92)) * (pH / 2) * 0.96
      if (amp < 0.35) continue
      ctx.strokeStyle = inEvalRange(b.tc) ? primaryWaveColor : outsideWaveColor
      ctx.beginPath()
      ctx.moveTo(b.x, mY - amp)
      ctx.lineTo(b.x, mY + amp)
      ctx.stroke()
    }
    ctx.lineCap = "butt"
    ctx.strokeStyle = baselineColor
    ctx.lineWidth = 0.8
    ctx.beginPath()
    ctx.moveTo(pL, mY)
    ctx.lineTo(pL + pW, mY)
    ctx.stroke()
  }
  if (state.waveformView === "raw") {
    // Raw — connected source-reference trace.
    plotTrace(RAW, absMax(RAW) + 1e-9, "rgba(148,163,184,0.68)", 0.9, 0.95)
  } else {
    plotCleanTrace()
  }
  if (!gateRejected && state.layers.murmur) {
    const energyGuide = visibleEnergyGuide()
    const len = energyGuide.length,
      dt = D.duration / len
    const [i0, i1] = visibleRange(len)
    if (i1 > i0) {
      if (state.energyGuideMode === "coherent") {
        // Two-state research display: the complete selected-band
        // envelope remains visible in grey; only the portion that
        // passes cardiac-phase recurrence is overlaid in red.
        // Keep the released 100-300 Hz Energy envelope as a stable
        // grey reference. The sweep changes only the coherent red
        // overlay, so research gating never erases audible context.
        const background = MURMUR || []
        ctx.fillStyle = "rgba(148,163,184,0.10)"
        ctx.beginPath()
        for (let i = i0; i < i1; i++) {
          const t = i * dt
          const x = tToX(t, pL, pW)
          const sourceIndex = Math.min(
            background.length - 1,
            Math.max(0, Math.round((i / Math.max(1, len - 1)) * Math.max(0, background.length - 1)))
          )
          const y = mY - (background[sourceIndex] || 0) * (pH / 2) * 0.7
          if (i === i0) {
            ctx.moveTo(x, mY)
            ctx.lineTo(x, y)
          } else ctx.lineTo(x, y)
        }
        ctx.lineTo(tToX((i1 - 1) * dt, pL, pW), mY)
        ctx.closePath()
        ctx.fill()
        ctx.strokeStyle = "rgba(148,163,184,0.34)"
        ctx.lineWidth = 0.9
        ctx.beginPath()
        for (let i = i0; i < i1; i++) {
          const t = i * dt
          const x = tToX(t, pL, pW)
          const sourceIndex = Math.min(
            background.length - 1,
            Math.max(0, Math.round((i / Math.max(1, len - 1)) * Math.max(0, background.length - 1)))
          )
          const y = mY - (background[sourceIndex] || 0) * (pH / 2) * 0.7
          if (i === i0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.stroke()
      }
      const baseActivity = visualAcousticActivityStats()
      const activity = phaseReviewResolution === "alternate" ? phaseAlternateActivity(baseActivity) : baseActivity
      const sysRepeat = activity ? Math.max(0, Math.min(1, activity.sysCyclePct / 100)) : 0.35
      const diaRepeat = activity ? Math.max(0, Math.min(1, activity.diaCyclePct / 100)) : 0.35
      const phaseIntervals = []
      const phasePeaks = visualGuidePeaks()
      for (let j = 0; j < phasePeaks.length - 1; j++) {
        const a = phasePeaks[j],
          b = phasePeaks[j + 1]
        if (a.l === "S1" && b.l === "S2") phaseIntervals.push({ start: a.t, end: b.t, repeat: sysRepeat })
        if (a.l === "S2" && b.l === "S1") phaseIntervals.push({ start: a.t, end: b.t, repeat: diaRepeat })
      }
      let phaseIndex = 0
      function phaseRepeatAt(t) {
        while (phaseIndex < phaseIntervals.length && phaseIntervals[phaseIndex].end < t) phaseIndex += 1
        const p = phaseIntervals[phaseIndex]
        return p && t >= p.start && t <= p.end ? p.repeat : 0.15
      }
      function localStrength(v) {
        return Math.max(0, Math.min(1, (v - 0.05) / 0.75))
      }
      const evalRange = mvp2EvalGuideRange()
      ctx.lineWidth = 1
      for (let i = i0; i < i1 - 1; i++) {
        const t0 = i * dt,
          t1 = (i + 1) * dt
        const tc = (t0 + t1) / 2
        const inEvalRange = evalRange ? tc >= evalRange.start && tc <= evalRange.end : true
        const x0 = tToX(t0, pL, pW),
          x1 = tToX(t1, pL, pW)
        const y0 = mY - energyGuide[i] * (pH / 2) * 0.7
        const y1 = mY - energyGuide[i + 1] * (pH / 2) * 0.7
        const repeat = phaseRepeatAt(tc)
        const strength = localStrength(Math.max(energyGuide[i], energyGuide[i + 1]))
        const evidence = Math.max(0, Math.min(1, 0.35 * strength + 0.65 * strength * repeat))
        // Upstream's in-range alphas (0.05 + 0.34e / 0.22 + 0.62e) also apply
        // without a range; a guide without one keeps the alphas it was drawn
        // with before the engine.
        const fillAlpha = evalRange
          ? inEvalRange
            ? 0.05 + evidence * 0.34
            : 0.025 + evidence * 0.08
          : 0.025 + evidence * 0.22
        const strokeAlpha = evalRange
          ? inEvalRange
            ? 0.22 + evidence * 0.62
            : 0.1 + evidence * 0.2
          : 0.18 + evidence * 0.52
        ctx.fillStyle = `rgba(239,68,68,${fillAlpha.toFixed(3)})`
        ctx.beginPath()
        ctx.moveTo(x0, mY)
        ctx.lineTo(x0, y0)
        ctx.lineTo(x1, y1)
        ctx.lineTo(x1, mY)
        ctx.closePath()
        ctx.fill()
        ctx.strokeStyle = `rgba(239,68,68,${strokeAlpha.toFixed(3)})`
        ctx.beginPath()
        ctx.moveTo(x0, y0)
        ctx.lineTo(x1, y1)
        ctx.stroke()
      }
    }
  }

  if (!gateRejected && state.layers.s1s2) {
    // S1/S2 timing guidance only; source/confidence stay in audit mode.
    visualGuidePeaks().forEach((p) => {
      if (p.t < view.start || p.t > tEnd) return
      const outsideEvalContext = mvp2OutsideEvalGuideContext(p.t)
      const isNoisy = state.layers.noise && overlapsNoiseWindow(p.on, p.off)
      const isInferred = p.source === "inferred" || p.promoted === true
      const xPk = tToX(p.t, pL, pW)
      const isS1 = p.l === "S1"
      const strokeC = isNoisy
        ? "rgba(148,163,184,0.18)"
        : outsideEvalContext
        ? isS1
          ? "rgba(148,163,184,0.24)"
          : "rgba(100,116,139,0.22)"
        : isInferred
        ? isS1
          ? "rgba(148,163,184,0.48)"
          : "rgba(100,116,139,0.44)"
        : isS1
        ? "rgba(148,163,184,0.78)"
        : "rgba(100,116,139,0.72)"
      const labelC = outsideEvalContext ? "#8290a3" : isInferred ? "#a9b5c5" : isS1 ? "#e2e8f0" : "#b8c3d1"
      const confidence = Math.max(0, Math.min(1, Number(p.confidence ?? 0.75)))
      const topPad = isS1 ? 5 : 14
      const bottomPad = isS1 ? 9 : 18
      const markerAlpha = outsideEvalContext ? 0.34 : isNoisy ? 0.18 : 0.42 + confidence * 0.46
      const lineTop = pT + topPad
      const lineBottom = pT + pH - bottomPad
      ctx.strokeStyle = strokeC
      ctx.globalAlpha = markerAlpha
      ctx.lineWidth = isInferred ? 1.0 : isS1 ? 1.35 : 1.1
      ctx.setLineDash(isInferred ? [4, 4] : [])
      ctx.beginPath()
      ctx.moveTo(xPk, lineTop)
      ctx.lineTo(xPk, lineBottom)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.globalAlpha = 1
      // Label
      if (!isNoisy) {
        ctx.fillStyle = labelC
        ctx.font = "bold 11px sans-serif"
        ctx.textAlign = "center"
        ctx.fillText(isInferred ? `${p.l}*` : p.l, xPk, isS1 ? pT + 14 : pT + 27)
      }
    })
  }

  // Caution overlay is drawn last so unreliable local regions do not look
  // like equally trustworthy Sys/Dia or S1/S2 guides.
  if (!gateRejected && state.layers.noise) {
    quality.noiseWindows.forEach((w) => {
      if (w.end < view.start || w.start > tEnd) return
      const x1 = tToX(Math.max(w.start, view.start), pL, pW)
      const x2 = tToX(Math.min(w.end, tEnd), pL, pW)
      drawCautionWindow(ctx, x1, x2, pT, pH)
    })
  }
  drawPlayhead(ctx, pL, pW, pT, pH, { visibleOnly: true })
}
